import React, { useMemo, useState } from 'react';
import type { Order, OrderItem, PaymentItemAllocation } from '../../types';
import {
  computeSubBillTotals,
  getItemLineId,
  getPaidQuantityForItem,
  getUnpaidQuantityForItem,
  isOrderItemDiscountLine,
  type SubBillDiscountAllocationMode,
  type SubBillSelection,
} from '../../lib/orderUtils';
import { useSoftKeyboard } from '../../contexts/SoftKeyboardContext';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import PlusIcon from '../icons/PlusIcon';
import MinusIcon from '../icons/MinusIcon';
import PrinterIcon from '../icons/PrinterIcon';

export interface SubBillBuilderResult {
  selections: SubBillSelection[];
  customerName: string;
  discountAllocationMode: SubBillDiscountAllocationMode;
  total: number;
  allocations: PaymentItemAllocation[];
}

interface SubBillBuilderProps {
  order: Order;
  onCancel: () => void;
  /**
   * Cashier confirmed the sub-bill — open card/cash/bank flow with this total pre-filled.
   * The parent (PaymentModal) is responsible for actually charging.
   */
  onPay: (result: SubBillBuilderResult) => void;
  /**
   * Cashier wants to print this sub-bill as a "bill / order" preview without taking payment yet.
   * Useful for handing the customer their slip before they pay.
   */
  onPrintBillOnly?: (result: SubBillBuilderResult) => void;
}

interface PickerLine {
  item: OrderItem;
  index: number;
  lineId: string;
  totalQty: number;
  paidQty: number;
  unpaidQty: number;
  /** Currently picked quantity to put into this sub-bill. */
  picked: number;
}

const SubBillBuilder: React.FC<SubBillBuilderProps> = ({
  order,
  onCancel,
  onPay,
  onPrintBillOnly,
}) => {
  const { closeKeyboard } = useSoftKeyboard();
  // Build the initial picker rows, hiding lines that are removed, fully paid, or pure discount lines.
  const initialLines = useMemo<PickerLine[]>(() => {
    const items = order.items ?? [];
    return items
      .map((item, index) => {
        const lineId = getItemLineId(item, index);
        const totalQty = item.quantity ?? 0;
        const paidQty = getPaidQuantityForItem(item);
        const unpaidQty = getUnpaidQuantityForItem(item);
        return {
          item,
          index,
          lineId,
          totalQty,
          paidQty,
          unpaidQty,
          picked: 0,
        } as PickerLine;
      })
      .filter((row) => {
        if (row.item.removed) return false;
        if (isOrderItemDiscountLine(row.item)) return false;
        return row.unpaidQty > 0;
      });
  }, [order.items]);

  const [pickedByLineId, setPickedByLineId] = useState<Record<string, number>>({});
  const [customerName, setCustomerName] = useState('');
  const [mode, setMode] = useState<SubBillDiscountAllocationMode>('proportional');

  const customerNameField = useSoftKeyboardField({
    id: 'subbill-customer-name',
    type: 'alphanumeric',
    label: "Customer name (e.g. John, Guest 2)",
    value: customerName,
    onChange: setCustomerName,
  });

  // Does the order have ANY allocatable discount/delivery? Only then show the toggle.
  const hasAllocatableExtras = useMemo(() => {
    const rewardDiscount = order.appliedReward ? order.appliedReward.discountValue : 0;
    const method = order.discountMethod ?? 'manual';
    const manualDiscount = method === 'manual' ? order.manualDiscount ?? 0 : 0;
    const firstTableDiscount =
      method === 'first-table' ? Math.max(0, order.firstTableDiscountAmount ?? 0) : 0;
    const couponDiscount = order.couponDiscountAmount ?? 0;
    const residentDiscount = order.residentDiscountAmount ?? 0;
    const delivery =
      order.orderType === 'delivery' ? Math.max(0, order.deliveryCharge ?? 0) : 0;
    return (
      rewardDiscount + manualDiscount + firstTableDiscount + couponDiscount + residentDiscount + delivery
    ) > 0;
  }, [order]);

  // Build the live picker view (apply current pickedByLineId values).
  const lines: PickerLine[] = useMemo(
    () =>
      initialLines.map((row) => ({
        ...row,
        picked: Math.min(row.unpaidQty, Math.max(0, pickedByLineId[row.lineId] ?? 0)),
      })),
    [initialLines, pickedByLineId]
  );

  const selections: SubBillSelection[] = useMemo(
    () =>
      lines
        .filter((row) => row.picked > 0)
        .map((row) => ({ lineId: row.lineId, quantity: row.picked })),
    [lines]
  );

  const totals = useMemo(
    () => computeSubBillTotals(order, selections, mode),
    [order, selections, mode]
  );

  const totalPickedCount = selections.reduce((s, sel) => s + sel.quantity, 0);

  const setPicked = (lineId: string, qty: number, max: number) => {
    closeKeyboard();
    const clamped = Math.max(0, Math.min(max, Math.floor(qty)));
    setPickedByLineId((prev) => ({ ...prev, [lineId]: clamped }));
  };

  const selectAll = () => {
    closeKeyboard();
    const next: Record<string, number> = {};
    for (const row of initialLines) {
      next[row.lineId] = row.unpaidQty;
    }
    setPickedByLineId(next);
  };

  const clearAll = () => { closeKeyboard(); setPickedByLineId({}); };

  const buildResult = (): SubBillBuilderResult => ({
    selections,
    customerName: customerName.trim(),
    discountAllocationMode: mode,
    total: totals.total,
    allocations: totals.allocations,
  });

  const canSubmit = selections.length > 0 && totals.total > 0 && !totals.hasOverflow;

  return (
    <div className="space-y-4">
      {/* Items picker — shown first so opening the name keyboard doesn't cover it */}
      <div className="border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
          <span className="text-xs font-semibold text-gray-700">
            Pick items for this customer ({totalPickedCount} of {initialLines.reduce((s, r) => s + r.unpaidQty, 0)} unpaid)
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={selectAll}
              className="text-[11px] sm:text-xs text-pink-600 hover:text-pink-700 font-semibold"
              disabled={initialLines.length === 0}
            >
              Select all
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={clearAll}
              className="text-[11px] sm:text-xs text-gray-600 hover:text-gray-800 font-semibold"
              disabled={selections.length === 0}
            >
              Clear
            </button>
          </div>
        </div>
        {initialLines.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            All items on this order are already paid for.
          </div>
        ) : (
          <ul className="divide-y">
            {lines.map((row) => {
              const unitPrice = row.item.price;
              const lineTotalForPicked = unitPrice * row.picked;
              return (
                <li key={row.lineId} className="px-3 py-2 sm:px-4 sm:py-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm sm:text-base text-gray-900 break-words">
                        {row.item.name}
                        {row.item.selectedMeatOption && (
                          <span className="ml-2 text-[10px] sm:text-xs font-normal text-pink-600 bg-pink-100 px-1.5 py-0.5 rounded">
                            {row.item.selectedMeatOption}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] sm:text-xs text-gray-500">
                        ${unitPrice.toFixed(2)} each ·{' '}
                        {row.paidQty > 0 ? (
                          <span className="text-green-600 font-semibold">
                            {row.paidQty}/{row.totalQty} already paid
                          </span>
                        ) : (
                          <span>{row.unpaidQty} unpaid</span>
                        )}
                      </p>
                    </div>
                    <p className="font-semibold text-sm sm:text-base text-gray-900 flex-shrink-0">
                      ${lineTotalForPicked.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPicked(row.lineId, row.picked - 1, row.unpaidQty)}
                        disabled={row.picked <= 0}
                        className="p-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Decrease"
                      >
                        <MinusIcon className="w-4 h-4" />
                      </button>
                      <span className="inline-flex items-center justify-center min-w-[3rem] px-2 py-1 bg-gray-100 text-gray-800 text-sm font-semibold rounded">
                        {row.picked} / {row.unpaidQty}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPicked(row.lineId, row.picked + 1, row.unpaidQty)}
                        disabled={row.picked >= row.unpaidQty}
                        className="p-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Increase"
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                    </div>
                    {row.picked > 0 && row.picked < row.unpaidQty && (
                      <button
                        type="button"
                        onClick={() => setPicked(row.lineId, row.unpaidQty, row.unpaidQty)}
                        className="text-[11px] text-pink-600 hover:text-pink-700 font-semibold"
                      >
                        All {row.unpaidQty}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Customer name + discount allocation — below items so keyboard doesn't cover the picker */}
      <div className="space-y-3">
        <div>
          <label htmlFor="subbill-name" className="block text-xs font-semibold text-gray-700 mb-1">
            Customer name (optional, prints on receipt)
          </label>
          <input
            id="subbill-name"
            type="text"
            value={customerName}
            {...customerNameField}
            placeholder="e.g. John, Guest 2"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            maxLength={40}
          />
        </div>
        {hasAllocatableExtras && (
          <div>
            <p className="block text-xs font-semibold text-gray-700 mb-1">
              Order has discount / delivery — apply to this sub-bill how?
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { closeKeyboard(); setMode('proportional'); }}
                className={`text-xs sm:text-sm py-2 px-3 rounded-md border transition-colors ${
                  mode === 'proportional'
                    ? 'bg-pink-500 text-white border-pink-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                title="Each customer gets a fair share based on their items' subtotal"
              >
                Proportional
              </button>
              <button
                type="button"
                onClick={() => { closeKeyboard(); setMode('remainder'); }}
                className={`text-xs sm:text-sm py-2 px-3 rounded-md border transition-colors ${
                  mode === 'remainder'
                    ? 'bg-pink-500 text-white border-pink-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                title="This customer pays face value; discount/delivery stays on the remaining balance"
              >
                Add to remainder
              </button>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              {mode === 'proportional'
                ? 'Discount and delivery are split across items by their subtotal share.'
                : 'This customer pays the items at face value (incl. GST). Discount/delivery stay on the last person.'}
            </p>
          </div>
        )}
      </div>

      {/* Live summary */}
      <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm">
        <p className="text-xs font-semibold text-gray-700 mb-2">Sub-bill summary</p>
        <div className="space-y-1 text-gray-700">
          <div className="flex justify-between">
            <span>Items subtotal (incl. GST)</span>
            <span>${totals.itemsSubtotal.toFixed(2)}</span>
          </div>
          {totals.allocatedDiscount > 0 && (
            <div className="flex justify-between text-blue-700">
              <span>Allocated discount share</span>
              <span>-${totals.allocatedDiscount.toFixed(2)}</span>
            </div>
          )}
          {totals.allocatedDelivery > 0 && (
            <div className="flex justify-between text-pink-700">
              <span>Allocated delivery share</span>
              <span>+${totals.allocatedDelivery.toFixed(2)}</span>
            </div>
          )}
          {totals.allocatedHoliday > 0 && (
            <div className="flex justify-between text-amber-800">
              <span>Allocated holiday surcharge</span>
              <span>+${totals.allocatedHoliday.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-xs text-gray-600">
            <span>Subtotal (ex. GST)</span>
            <span>${totals.netSubtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-600">
            <span>GST (15%)</span>
            <span>${totals.gst.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-base font-bold text-gray-900 pt-1 border-t border-gray-300 mt-1">
            <span>Sub-bill total</span>
            <span>${totals.total.toFixed(2)}</span>
          </div>
        </div>
        {totals.hasOverflow && (
          <p className="mt-2 text-xs text-red-600 font-semibold">
            Some items were already paid by another register. Adjust your selection.
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => { closeKeyboard(); onCancel(); }}
          className="py-2.5 px-4 rounded-lg bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 text-sm"
        >
          Cancel
        </button>
        {onPrintBillOnly && (
          <button
            type="button"
            onClick={() => { closeKeyboard(); if (canSubmit) onPrintBillOnly(buildResult()); }}
            disabled={!canSubmit}
            className="py-2.5 px-4 rounded-lg bg-blue-100 text-blue-800 font-semibold hover:bg-blue-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
          >
            <PrinterIcon className="w-4 h-4" />
            Print bill only
          </button>
        )}
        <button
          type="button"
          onClick={() => { closeKeyboard(); if (canSubmit) onPay(buildResult()); }}
          disabled={!canSubmit}
          className={`py-2.5 px-4 rounded-lg font-semibold text-white text-sm ${
            canSubmit ? 'bg-pink-500 hover:bg-pink-600' : 'bg-gray-400 cursor-not-allowed'
          } ${onPrintBillOnly ? '' : 'sm:col-span-2'}`}
        >
          {canSubmit ? `Pay $${totals.total.toFixed(2)} →` : 'Pick items to continue'}
        </button>
      </div>
    </div>
  );
};

export default SubBillBuilder;
